import type { ValidationError } from './types';

/**
 * Shared cross-field validators for Mission sub-KPI types.
 *
 * Per-field constraints (min/max/required) live in each type's
 * config_schema and are checked by the wizard before save. These helpers
 * cover the cross-field rules that the per-field schema can't express:
 * "warning days must be greater than critical days", "tracking start must
 * not be after deadline", etc.
 *
 * Each helper takes a partial config and returns either null (valid) or a
 * human-readable error message. Callers compose them in their type's
 * `validate_config` function and return the first error encountered.
 *
 * Skip's principle (DEV-814): tolerance settings should always be
 * internally consistent. "Yellow within 30 days, Red within 30 days" makes
 * no sense — the yellow band must be wider than the red band. Likewise,
 * "tracking starts after the deadline" is incoherent. Catch these at save
 * time so users don't ship broken thresholds.
 */

/**
 * Deadline windows: yellow_window must be STRICTLY wider than the red
 * window. Equality is rejected because it would collapse the yellow band
 * to nothing — the user would see Green flip directly to Red on the day
 * the warning window opens.
 *
 * Returns null if either field is unset (cross-field rule only applies
 * when both are explicitly configured).
 */
export function validateDeadlineWindows(config: {
  warning_days?: number;
  critical_days?: number;
}): ValidationError | null {
  const w = config.warning_days;
  const c = config.critical_days;
  if (typeof w !== 'number' || typeof c !== 'number') return null;
  if (!Number.isFinite(w) || !Number.isFinite(c)) return null;
  if (w < 0) return { field: 'warning_days', message: 'Warning days must be zero or greater.' };
  if (c < 0) return { field: 'critical_days', message: 'Critical days must be zero or greater.' };
  if (w <= c) {
    // Pin the error to the red field — that's typically the one the user
    // edited last, and it's the natural place to read "must be smaller than yellow."
    return {
      field: 'critical_days',
      message: 'Red days must be SMALLER than Yellow days. Yellow needs to open earlier than red as the deadline approaches.',
    };
  }
  return null;
}

/**
 * Score color bands: the YELLOW threshold (where green ends and yellow
 * begins, stored as `green_threshold`) must be STRICTLY greater than the
 * RED threshold (where yellow ends and red begins, stored as
 * `yellow_threshold`). Equality collapses the yellow band to nothing.
 *
 * Note on the field naming oddity (DEV-814): the user-facing labels say
 * "Yellow when % drops below" and "Red when % drops below", but the
 * underlying field names are still green_threshold and yellow_threshold
 * for DB-schema-stability reasons. The number stored in `green_threshold`
 * is the FLOOR of green = the CEILING of yellow. The number stored in
 * `yellow_threshold` is the FLOOR of yellow = the CEILING of red.
 *
 * Returns null if either field is unset.
 */
export function validateScoreBands(config: {
  green_threshold?: number;
  yellow_threshold?: number;
}): ValidationError | null {
  const g = config.green_threshold;
  const y = config.yellow_threshold;
  if (typeof g !== 'number' || typeof y !== 'number') return null;
  if (!Number.isFinite(g) || !Number.isFinite(y)) return null;
  if (g < 0 || g > 100) return { field: 'green_threshold', message: 'Yellow threshold must be between 0 and 100.' };
  if (y < 0 || y > 100) return { field: 'yellow_threshold', message: 'Red threshold must be between 0 and 100.' };
  if (g <= y) {
    return {
      field: 'yellow_threshold',
      message: 'Red % must be SMALLER than Yellow %. Yellow needs to open earlier than red as the % falls.',
    };
  }
  return null;
}

/**
 * Tracking start date must not be after the deadline date. A KPI that
 * starts tracking after its deadline can never be measured.
 *
 * Returns null if either date is unset (it's valid to have one without
 * the other).
 */
export function validateLifecycleDates(config: {
  tracking_start_date?: string;
  deadline_date?: string;
}): ValidationError | null {
  const start = config.tracking_start_date;
  const end = config.deadline_date;
  if (!start || !end) return null;
  // String compare works for ISO YYYY-MM-DD format. Pin to deadline_date
  // because in practice the deadline is the field most often being edited
  // when this rule trips (user pulls deadline earlier and accidentally
  // pulls it before the tracking start). The message explicitly names BOTH
  // fields so the user understands the constraint.
  if (start > end) {
    return {
      field: 'deadline_date',
      message: `Target deadline (${end}) cannot be before Tracking start date (${start}). Move the deadline later than the tracking start, or move the tracking start earlier than the deadline.`,
    };
  }
  return null;
}

/**
 * Pace-mode tolerances (DEV-815): yellow_units_behind must be STRICTLY less
 * than red_units_behind. Same shape as score bands and deadline windows —
 * the yellow band needs a non-empty range below red.
 *
 * Returns null if either field is unset.
 */
export function validatePaceTolerances(config: {
  pace_yellow_units_behind?: number;
  pace_red_units_behind?: number;
}): ValidationError | null {
  const y = config.pace_yellow_units_behind;
  const r = config.pace_red_units_behind;
  if (typeof y !== 'number' || typeof r !== 'number') return null;
  if (!Number.isFinite(y) || !Number.isFinite(r)) return null;
  if (y < 0) return { field: 'pace_yellow_units_behind', message: 'Yellow units behind must be zero or greater.' };
  if (r < 0) return { field: 'pace_red_units_behind', message: 'Red units behind must be zero or greater.' };
  if (y >= r) {
    // Pin the error to the RED field — Pace inverts the usual ordering
    // (yellow < red instead of yellow > red), and pinning to red surfaces
    // the message at the typical place users edit. The body explicitly
    // calls out the inversion so users coming from Score/Deadline don't
    // get confused.
    return {
      field: 'pace_red_units_behind',
      message: 'For Pace, Red units behind must be GREATER than Yellow units behind. (Pace inverts the usual ordering — a smaller "units behind" number means yellow opens earlier on the way behind.)',
    };
  }
  return null;
}

/**
 * Pace-mode prerequisite (DEV-816 / Skip): if color_mode === 'pace', BOTH
 * tracking_start_date and deadline_date must be populated. Without them,
 * the linear-interpolation math has no endpoints and applyColorModePace-
 * Scoring silently falls back to base score — which would ship a broken
 * configuration the user thought was driving Pace coloring.
 *
 * The wizard also disables the Pace card in the UI when the predicate is
 * false, but a determined user could pick Pace, then clear a date afterward.
 * This validator catches that case at save.
 *
 * Returns null when color_mode is anything other than 'pace' (the rule
 * doesn't apply). Returns null when both dates are populated.
 */
export function validatePaceModeRequiresDates(config: {
  color_mode?: string;
  tracking_start_date?: string;
  deadline_date?: string;
}): ValidationError | null {
  if (config.color_mode !== 'pace') return null;
  const hasStart = !!config.tracking_start_date;
  const hasEnd = !!config.deadline_date;
  if (hasStart && hasEnd) return null;
  if (!hasStart && !hasEnd) {
    // Pin to color_mode since this is really "your mode choice can't be
    // satisfied" — that puts the error at the mode picker so the user can
    // either fix the dates or pick a different mode.
    return {
      field: 'color_mode',
      message: 'Pace mode requires both Tracking start date and Target deadline. Set them or pick a different color mode.',
    };
  }
  if (!hasStart) {
    return {
      field: 'tracking_start_date',
      message: 'Pace mode requires a Tracking start date. Set it or pick a different color mode.',
    };
  }
  return {
    field: 'deadline_date',
    message: 'Pace mode requires a Target deadline. Set it or pick a different color mode.',
  };
}

/**
 * Compose all common cross-field validations and return the first error.
 * Callers should still run their type-specific rules (target > baseline,
 * etc.) in addition to this composer.
 *
 * Mode-aware threshold ordering (DEV-816 / Skip): only the ACTIVE color
 * mode's threshold-ordering rule is checked. Stale values for hidden mode
 * fields (e.g., user typed something invalid in Score thresholds, then
 * switched to Deadline mode) shouldn't block save because those values
 * don't affect runtime scoring under the new mode. Lifecycle and Pace-
 * gating rules are mode-independent and always run.
 */
export function validateCommonCrossFields(config: {
  color_mode?: string;
  warning_days?: number;
  critical_days?: number;
  green_threshold?: number;
  yellow_threshold?: number;
  tracking_start_date?: string;
  deadline_date?: string;
  pace_yellow_units_behind?: number;
  pace_red_units_behind?: number;
}): ValidationError | null {
  const mode = config.color_mode ?? 'score';
  const thresholdError =
    mode === 'deadline' ? validateDeadlineWindows(config) :
    mode === 'pace'     ? validatePaceTolerances(config)  :
                          validateScoreBands(config);
  return (
    thresholdError ||
    validateLifecycleDates(config) ||
    validatePaceModeRequiresDates(config)
  );
}
